import * as React from 'react';

import { Badge, RawTooltip, TooltipTrigger } from '@rematter/ui-web';

import { config } from '~/config';
import { useApiHealth } from '~/shared/hooks';

interface VersionBadgeProps {
  /**
   * Whether the sidebar is collapsed (hides the badge when true)
   */
  collapsed?: boolean;
}

/**
 * Displays the frontend version with API health monitoring
 *
 * Features:
 * - Shows frontend version from config
 * - Monitors API health every 30 seconds
 * - Badge color changes based on health status (neutral=healthy, red=unhealthy)
 * - Tooltip shows API version on hover
 */
export const VersionBadge: React.FC<VersionBadgeProps> = ({ collapsed }) => {
  const { isHealthy, apiVersion } = useApiHealth();

  // Don't render when collapsed to match sidebar pattern
  if (collapsed) {
    return null;
  }

  const frontendVersion = config.service.version;

  return (
    <TooltipTrigger>
      <Badge
        className="cursor-pointer"
        color={isHealthy ? 'neutral' : 'failure'}
        size="xs"
      >
        v{frontendVersion}
      </Badge>
      <RawTooltip placement="top" offset={8}>
        <div className="text-xs">
          <div className="font-semibold">Frontend: v{frontendVersion}</div>
          {apiVersion && <div className="mt-1">API: v{apiVersion}</div>}
          {!isHealthy && (
            <div className="mt-1 text-failure/60">API Unavailable</div>
          )}
        </div>
      </RawTooltip>
    </TooltipTrigger>
  );
};
